import { CUSTOM_FIELD_NAME_MAX_LENGTH } from "@jugl-web/domain-resources/tasks";
import { Button } from "@jugl-web/ui-components/cross-platform/Button";
import { Checkbox } from "@jugl-web/ui-components/cross-platform/Checkbox";
import { TextInput } from "@jugl-web/ui-components/cross-platform/forms/TextInput";
import {
  Select,
  SelectItem,
} from "@jugl-web/ui-components/cross-platform/forms/Select";
import { cx, useTranslations } from "@jugl-web/utils";
import { FC, ReactNode, forwardRef, useMemo } from "react";
import {
  SupportedCustomFieldType,
  useCustomFieldTypes,
} from "../../hooks/useCustomFieldTypes";
import { CustomFieldIcon } from "../CustomFieldIcon";

interface CustomFieldFromProps {
  children: ReactNode;
  className?: string;
}

export const CustomFieldForm = ({
  children,
  className,
}: CustomFieldFromProps) => (
  <div className={cx("flex w-full max-w-[500px] flex-col px-8", className)}>
    {children}
  </div>
);

interface HeadingProps {
  title: ReactNode;
  subtitle?: ReactNode;
  className?: string;
}

CustomFieldForm.Heading = (({ title, subtitle, className }) => (
  <div className={cx("flex flex-col items-center gap-2", className)}>
    <h3 className="m-0 text-center font-secondary text-lg font-medium text-dark">
      {title}
    </h3>
    {subtitle && (
      <h2 className="m-0 text-center font-secondary text-sm leading-[21px] font-normal text-dark-600">
        {subtitle}
      </h2>
    )}
  </div>
)) as FC<HeadingProps>;

interface FieldInputProps {
  label: string;
  value: string;
  placeholder?: string;
  isDisabled?: boolean;
  errorMessage?: string;
  className?: string;
  onChange: (name: string) => void;
}

CustomFieldForm.FieldInput = forwardRef<HTMLInputElement, FieldInputProps>(
  (
    {
      label,
      value,
      placeholder,
      isDisabled,
      className,
      errorMessage,
      onChange,
    },
    ref
  ) => (
    <TextInput
      ref={ref}
      isRequired
      label={label}
      type="text"
      placeholder={placeholder}
      isDisabled={isDisabled}
      value={value}
      maxLength={CUSTOM_FIELD_NAME_MAX_LENGTH}
      lengthIndicator={{
        current: value.length,
        max: CUSTOM_FIELD_NAME_MAX_LENGTH,
      }}
      onChange={(event) => onChange(event.target.value)}
      classNames={{ wrapperClassName: className }}
      errorMessage={errorMessage}
      isInvalid={!!errorMessage}
    />
  )
);

interface FieldTypeSelectProps<TFieldType extends SupportedCustomFieldType> {
  fieldTypes: TFieldType[];
  selectedType: TFieldType;
  isDisabled?: boolean;
  className?: string;
  onSelect?: (type: TFieldType) => void;
}

const FieldTypeSelect = <TFieldType extends SupportedCustomFieldType>({
  fieldTypes,
  selectedType,
  isDisabled,
  className,
  onSelect,
}: FieldTypeSelectProps<TFieldType>) => {
  const { t } = useTranslations();
  const { customFieldTypeToDetails } = useCustomFieldTypes();

  const items = useMemo(
    () =>
      fieldTypes.map<SelectItem>((type) => ({
        name: customFieldTypeToDetails[type].shortLabel,
        value: type,
        icon: <CustomFieldIcon type={type} />,
        labelClassName: "text-sm",
      })),
    [customFieldTypeToDetails, fieldTypes]
  );

  return (
    <Select
      isRequired
      label={t({
        id: "tasks-page.custom-field-type",
        defaultMessage: "Field type",
      })}
      value={selectedType}
      items={items}
      onSelect={(value) => onSelect?.(value as TFieldType)}
      isDisabled={!onSelect || isDisabled}
      classNames={{ wrapperClassName: className }}
    />
  );
};

CustomFieldForm.FieldTypeSelect = FieldTypeSelect;

interface IsShownInCardSwitchProps {
  isChecked: boolean;
  className?: string;
  onChange: (value: boolean) => void;
}

CustomFieldForm.IsShownInCardCheckbox = (({
  isChecked,
  className,
  onChange,
}) => {
  const { t } = useTranslations();

  return (
    <div className={className}>
      <Checkbox
        size="md"
        label={t({
          id: "tasks-page.visible-in-task-card",
          defaultMessage: "Visible in task card",
        })}
        labelClassName="text-dark-700 font-secondary text-xs leading-[140%] ml-0.5"
        isChecked={isChecked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </div>
  );
}) as FC<IsShownInCardSwitchProps>;

interface ActionsProps {
  submitButton: {
    label: string;
    isDisabled: boolean;
    onClick: () => void;
  };
  onCancel: () => void;
}

CustomFieldForm.Actions = (({ submitButton, onCancel }) => {
  const { t } = useTranslations();

  return (
    <div className="flex items-center justify-center gap-3.5">
      <Button
        variant="contained"
        color="grey"
        className="h-10 w-[143px] font-secondary font-medium"
        onClick={onCancel}
      >
        {t({
          id: "common.cancel",
          defaultMessage: "Cancel",
        })}
      </Button>
      <Button
        variant="contained"
        color="primary"
        className="h-10 w-[143px] font-secondary font-medium"
        isDisabled={submitButton.isDisabled}
        onClick={submitButton.onClick}
      >
        {submitButton.label}
      </Button>
    </div>
  );
}) as FC<ActionsProps>;
